import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaCheckCircle, FaDownload, FaArrowLeft } from "react-icons/fa";
import { getTxExplorerUrl } from "../../helper/x402/chains";

/**
 * Standalone post-payment receipt page at `/pay/receipt/:txHash`.
 *
 * Two data-source paths:
 *   1. Fast path — `location.state` carries `{ receipt, link, method }`
 *      handed over by `CheckoutPanel` when it navigates here on
 *      successful settlement. No fetch needed; rendering is instant.
 *   2. Fallback — on a refresh / direct load the state is gone.
 *      Look up the same payload in `sessionStorage` under the key
 *      `pay:receipt:{txHash}` (populated alongside the navigate by
 *      CheckoutPanel). If that's also empty, render an
 *      "unavailable — see explorer" stub so the tx hash is still
 *      reachable.
 *
 * We intentionally DON'T hit a backend endpoint here — shareable
 * receipts across sessions would need a server-side lookup by
 * txHash, which is a bigger backend change. sessionStorage covers
 * "refresh in the same tab" and "reopen from history" within the
 * browser session, which is what a payer needs post-payment in
 * practice.
 *
 * "Download PDF" uses `window.print()` with a `@media print` CSS
 * block that hides chrome + navigation. No JS PDF library, no
 * bundle cost — the browser's native print-to-PDF target produces
 * the file.
 */
export function ReceiptPage() {
  const { txHash } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Resolve the receipt/link/method from location.state first, then
  // sessionStorage. We do this once per mount + txHash via useMemo
  // so the render is stable — `location.state` changes identity on
  // every re-render even when its contents haven't.
  const resolved = useMemo(() => {
    if (location.state?.receipt && location.state?.link) {
      return {
        source: "state",
        receipt: location.state.receipt,
        link: location.state.link,
        method: location.state.method || null,
      };
    }
    try {
      const raw = sessionStorage.getItem(`pay:receipt:${txHash}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.receipt && parsed?.link) {
          return {
            source: "session",
            receipt: parsed.receipt,
            link: parsed.link,
            method: parsed.method || null,
          };
        }
      }
    } catch {
      // sessionStorage disabled (private mode / iframe) — fall through.
    }
    return null;
  }, [location.state, txHash]);

  // Re-hydrate sessionStorage when the data arrived via location.state.
  // That way a refresh immediately after landing here still finds the
  // receipt under the same key.
  const [cached, setCached] = useState(false);
  useEffect(() => {
    if (cached || !resolved || resolved.source !== "state") return;
    try {
      sessionStorage.setItem(
        `pay:receipt:${txHash}`,
        JSON.stringify({
          receipt: resolved.receipt,
          link: resolved.link,
          method: resolved.method,
        }),
      );
      setCached(true);
    } catch {
      // Storage unavailable — nothing we can do; in-memory is fine.
    }
  }, [cached, resolved, txHash]);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate("/");
    }
  };

  if (!resolved) {
    // No receipt context — either this URL was shared across
    // sessions or the tab was opened cold. Show an explorer
    // fallback so the tx hash is at least traceable.
    return (
      <div className="pay-receipt-page">
        <div className="pay-receipt-page__chrome">
          <button className="pay-back" onClick={handleBack}>
            <FaArrowLeft size={12} /> Back
          </button>
        </div>
        <div className="pay-receipt-page__unavailable">
          <h1>Receipt unavailable</h1>
          <p>
            This receipt's in-session data has expired. The transaction
            itself is still on-chain — use the explorer link below to
            verify it independently.
          </p>
          <p className="pay-receipt-page__txhash">
            <code>{txHash}</code>
          </p>
        </div>
      </div>
    );
  }

  const { receipt, link, method } = resolved;
  return <ReceiptBody receipt={receipt} link={link} method={method} />;
}

// ── Receipt body (the invoice-style layout) ───────────────────

function formatTimestamp(ms) {
  if (!ms || !Number.isFinite(Number(ms))) return null;
  try {
    const d = new Date(Number(ms));
    const date = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${date} at ${time}`;
  } catch {
    return new Date(Number(ms)).toISOString();
  }
}

function shortenAddress(addr) {
  if (!addr || addr.length < 10) return addr || "";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * Invoice-style receipt body. The same data ReceiptCard surfaces
 * on the inline success state, reformatted as a standalone
 * document: gradient header, FROM/TO parties in a two-column
 * block, line items, totals, a footer with the explorer link and
 * a "Download PDF" button.
 *
 * `@media print` in pay.css hides the back button + PDF button so
 * the printed output is just the document.
 */
function ReceiptBody({ receipt, link, method }) {
  // Normalize both payment-method receipt shapes (x402 snake_case
  // from the 402 Lambda; direct camelCase from /confirm-direct).
  const txHash =
    receipt?.receipt?.tx_hash || receipt?.receipt?.txHash || "pending";
  const paidBy =
    receipt?.receipt?.paid_by || receipt?.receipt?.from || null;
  const resolvedMethod =
    method || receipt?.receipt?.method || "x402";
  const amountDisplay =
    receipt?.amount_display ||
    receipt?.receipt?.amountDisplay ||
    link?.amountDisplay;
  const timestamp = receipt?.receipt?.timestamp;
  const explorerUrl = getTxExplorerUrl(link?.chainId, txHash);
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate("/");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="pay-receipt-page">
      {/* Controls — hidden when printing via @media print. */}
      <div className="pay-receipt-page__chrome">
        <button className="pay-back" onClick={handleBack} aria-label="Go back">
          <FaArrowLeft size={12} /> Back
        </button>
        <button
          type="button"
          className="pay-receipt-page__pdf-btn"
          onClick={handlePrint}
        >
          <FaDownload size={12} /> Download as PDF
        </button>
      </div>

      {/* The printable document. Everything inside here is what the
       * user gets in their PDF. */}
      <article className="pay-receipt-doc">
        <header className="pay-receipt-doc__header">
          <div className="pay-receipt-doc__brand">
            <div className="pay-receipt-doc__brand-name">Horus Wallet</div>
            <div className="pay-receipt-doc__brand-sub">Payment receipt</div>
          </div>
          <div className="pay-receipt-doc__status">
            <FaCheckCircle size={16} color="#1f6a1f" /> Paid
          </div>
        </header>

        <section className="pay-receipt-doc__meta">
          <div>
            <div className="pay-receipt-doc__label">Date</div>
            <div className="pay-receipt-doc__value">
              {formatTimestamp(timestamp) || "—"}
            </div>
          </div>
          <div>
            <div className="pay-receipt-doc__label">Method</div>
            <div className="pay-receipt-doc__value">
              {resolvedMethod === "direct"
                ? "Direct transfer"
                : "Gasless (x402)"}
            </div>
          </div>
          <div>
            <div className="pay-receipt-doc__label">Network</div>
            <div className="pay-receipt-doc__value">
              {link?.network || "—"}
            </div>
          </div>
        </section>

        <section className="pay-receipt-doc__parties">
          <div>
            <div className="pay-receipt-doc__label">From</div>
            <div className="pay-receipt-doc__value">
              {paidBy ? shortenAddress(paidBy) : "—"}
            </div>
            {paidBy && (
              <div className="pay-receipt-doc__subvalue">{paidBy}</div>
            )}
          </div>
          <div>
            <div className="pay-receipt-doc__label">To</div>
            <div className="pay-receipt-doc__value">
              {link?.creator?.username
                ? `@${link.creator.username}`
                : shortenAddress(link?.payTo)}
            </div>
            {link?.payTo && (
              <div className="pay-receipt-doc__subvalue">{link.payTo}</div>
            )}
          </div>
        </section>

        <section className="pay-receipt-doc__items">
          <table className="pay-receipt-doc__table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="pay-receipt-doc__num">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{link?.description || "Payment"}</td>
                <td className="pay-receipt-doc__num">{amountDisplay || "—"}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Network fee</td>
                <td className="pay-receipt-doc__num">
                  {resolvedMethod === "direct"
                    ? "Paid on-chain"
                    : "$0.00"}
                </td>
              </tr>
              <tr className="pay-receipt-doc__total-row">
                <td>Total</td>
                <td className="pay-receipt-doc__num">{amountDisplay || "—"}</td>
              </tr>
            </tfoot>
          </table>
        </section>

        <section className="pay-receipt-doc__tx">
          <div className="pay-receipt-doc__label">Transaction</div>
          <div className="pay-receipt-doc__value pay-receipt-doc__txhash">
            {txHash}
          </div>
          {explorerUrl && (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="pay-receipt-doc__explorer"
            >
              View on explorer →
            </a>
          )}
        </section>

        <footer className="pay-receipt-doc__footer">
          Signed + settled on-chain. horuswallet never held custody of
          the funds.
        </footer>
      </article>
    </div>
  );
}
